import React, { useRef } from "react";
import type { LoginPasswordDto } from "../../../../../api";
import { AppRoutes } from "../../../../../common/routes/app_routes";
import { useNavigate } from "react-router-dom";
import { useFormFields } from "../../../../../hooks/useFormFields";
import { Button } from "../../../../../components/common/button";
import { Input } from "../../../../../components/common/input";
import { Text } from "../../../../../components/common/typography";
import { usePasswordLogin } from "../../../hooks/usePasswordLogin";

export const PasswordLoginSection: React.FC = () => {
  const passwordRef = useRef<HTMLInputElement>(null!);
  const navigate = useNavigate();
  const { login, verifyTwoFactor, cancelTwoFactor, pendingMfaToken } = usePasswordLogin();
  const [twoFactorCode, setTwoFactorCode] = React.useState("");

  const { formFields, createChangeHandler } = useFormFields<LoginPasswordDto>({
    email: "",
    password: "",
  });

  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [errors, setErrors] = React.useState<
    Partial<Record<keyof LoginPasswordDto, string[]>>
  >({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setErrors({});

    try {
      await login(formFields.email, passwordRef.current.value);
    } catch (error: unknown) {
      const errorData = (
        error as { response?: { data?: { errors?: Record<string, string[]> } } }
      )?.response?.data;
      if (errorData?.errors) {
        setErrors(errorData.errors);
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  if (pendingMfaToken) {
    const handleVerify = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsLoggingIn(true);
      try {
        await verifyTwoFactor(twoFactorCode);
      } catch {
        // toast already shown
      } finally {
        setIsLoggingIn(false);
      }
    };

    return (
      <form onSubmit={handleVerify} className="flex w-full flex-col gap-6">
        <Text variant="body" tone="dark">
          Enter the 6-digit code we just emailed to {formFields.email}.
        </Text>
        <Input
          label="Verification code"
          inputMode="numeric"
          maxLength={6}
          value={twoFactorCode}
          onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456"
          autoComplete="one-time-code"
          required
        />
        <div className="flex w-full gap-2">
          <Button
            variant="ghost"
            type="button"
            onClick={() => {
              cancelTwoFactor();
              setTwoFactorCode("");
            }}
            disabled={isLoggingIn}
          >
            Back
          </Button>
          <Button
            variant="primary"
            type="submit"
            fullWidth
            disabled={isLoggingIn || twoFactorCode.length < 4}
          >
            {isLoggingIn ? "Verifying…" : "Verify and continue"}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-6">
      <Input
        label="Email address"
        type="email"
        name="email"
        value={formFields.email}
        onChange={createChangeHandler("email")}
        placeholder="email@example.com"
        autoComplete="email"
        required
        error={errors?.email?.[0]}
      />

      <Input
        label="Password"
        type="password"
        name="password"
        placeholder="Enter your password"
        autoComplete="current-password"
        required
        error={errors?.password?.[0]}
        ref={passwordRef}
      />

      <div className="flex w-full justify-end px-3">
        <button
          type="button"
          onClick={() => navigate(AppRoutes.RESET_PASSWORD)}
          className="bg-transparent"
        >
          <Text variant="bodySm" tone="body" inline>
            Forgot Password?
          </Text>
        </button>
      </div>

      <Button
        variant="primary"
        type="submit"
        fullWidth
        disabled={isLoggingIn || !formFields.email}
      >
        {isLoggingIn ? "Signing in..." : "Continue"}
      </Button>
    </form>
  );
};

export default PasswordLoginSection;
